/**
 * Enhanced Calculator with Step-by-Step Interface
 */

import React, { useState, useEffect } from 'react';
// import { useCalculatorStore } from '../../store/useCalculatorStore';
import { useAuthStore } from '../../store/useAuthStore';
import { Product, EnhancedShippingCalculation, CartItem } from '../../types';
import apiService from '../../services/api';
import DebugWindow from './DebugWindow';


const EnhancedCalculator: React.FC = () => {
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [destinationZip, setDestinationZip] = useState('');
  const [serviceLevel, setServiceLevel] = useState<'overnight' | 'second_day'>('overnight');
  const [zone, setZone] = useState<number | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [calculation, setCalculation] = useState<EnhancedShippingCalculation | null>(null);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [showDebugWindow, setShowDebugWindow] = useState(false);

  // Dry ice specifications
  const dryIceSpecs = {
    overnight: { count: 1, dimensions: { length: 5.75, width: 5.5, height: 1 }, weight: 0.5 },
    second_day: { count: 2, dimensions: { length: 5.75, width: 5.5, height: 1 }, weight: 0.5 }
  };

  useEffect(() => {
    loadProducts();
    // Temporarily disable system settings loading due to CORS issues
    // loadSystemSettings();
  }, [user]);


  const loadProducts = async () => {
    try {
      setProductsLoading(true);
      if (!user) return;
      
      console.log('Loading products for user:', user);
      console.log('User customer ID:', user.customerId);
      
      // First try without customer filter to see all products
      const response = await apiService.getProducts({ 
        customer_id: undefined, // Try without customer filter first
        active_only: true,
        size: 100 
      });
      console.log('Products response:', response);
      console.log('Response data:', response.data);
      
      // The backend returns ProductListResponse with structure: { products: [...], total: number, page: number, size: number }
      let productsArray: Product[] = [];
      
      if (response.data && typeof response.data === 'object') {
        // Check for the 'products' property which contains the array
        if ('products' in response.data && Array.isArray((response.data as any).products)) {
          productsArray = (response.data as any).products;
          console.log('Found products array with', productsArray.length, 'items');
        }
        // Fallback: check if response.data is directly an array
        else if (Array.isArray(response.data)) {
          productsArray = response.data;
          console.log('Response data is directly an array with', productsArray.length, 'items');
        }
        // Fallback: look for any array property
        else {
          const dataObj = response.data as any;
          for (const key in dataObj) {
            if (Array.isArray(dataObj[key])) {
              console.log(`Found array in property '${key}' with`, dataObj[key].length, 'items');
              productsArray = dataObj[key];
              break;
            }
          }
        }
      }
      
      if (productsArray.length > 0) {
        setProducts(productsArray);
        console.log('Successfully loaded products:', productsArray.length);
      } else {
        console.log('No products found in database for customer:', user?.customerId);
        setProducts([]);
        setError('No products available for your customer account. Please contact your administrator.');
      }
    } catch (error) {
      console.error('Failed to load products:', error);
      setError('Failed to load products');
      setProducts([]); // Ensure it's always an array
    } finally {
      setProductsLoading(false);
    }
  };

  const handleZipCodeChange = async (zip: string) => {
    setDestinationZip(zip);
    if (zip.length === 5) {
      try {
        const zoneData = await apiService.lookupZone(zip);
        setZone(zoneData.zone);
      } catch (error) {
        console.error('Failed to lookup zone:', error);
        // Fallback to estimation
        const lastThreeDigits = zip.slice(-3);
        const estimatedZone = Math.ceil(parseInt(lastThreeDigits) / 100);
        setZone(estimatedZone);
      }
    }
  };


  const updateQuantity = (product: Product, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(product.id);
      return;
    }
    
    const existingItem = cartItems.find(item => item.product.id === product.id);
    if (existingItem) {
      const updatedItem = {
        ...existingItem,
        quantity,
        totalWeight: product.weight * quantity,
        totalVolume: (product.length * product.width * product.height) * quantity
      };
      setCartItems(cartItems.map(item => 
        item.product.id === product.id ? updatedItem : item
      ));
    } else {
      const newItem: CartItem = {
        id: product.id.toString(),
        name: product.name,
        length: product.length,
        width: product.width,
        height: product.height,
        weight: product.weight,
        quantity,
        totalWeight: product.weight * quantity,
        totalVolume: (product.length * product.width * product.height) * quantity,
        product
      };
      setCartItems([...cartItems, newItem]);
    }
  };


  const removeFromCart = (productId: number) => {
    setCartItems(cartItems.filter(item => item.product.id !== productId));
  };

  const calculateShipping = async () => {
    console.log('=== CALCULATION DEBUG START ===');
    console.log('User:', user);
    console.log('Cart items:', cartItems);
    console.log('Destination ZIP:', destinationZip);
    console.log('Service level:', serviceLevel);
    console.log('Zone:', zone);

    if (!user || cartItems.length === 0) {
      console.log('ERROR: No user or cart items');
      setError('Please select at least one product');
      return;
    }

    if (!destinationZip || destinationZip.length !== 5) {
      console.log('ERROR: Invalid ZIP code');
      setError('Please enter a valid 5-digit ZIP code');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Convert cart items to individual items for packing
      const items = [];
      
      console.log('Processing cart items...');
      // Add regular products
      cartItems.forEach((cartItem, cartIndex) => {
        console.log(`Processing cart item ${cartIndex}:`, cartItem);
        for (let i = 0; i < cartItem.quantity; i++) {
          const item = {
            id: `${cartItem.product.id}-${i}`,
            name: cartItem.product.name,
            length: cartItem.product.length,
            width: cartItem.product.width,
            height: cartItem.product.height,
            weight: cartItem.product.weight,
            quantity: 1
          };
          console.log(`Adding product item ${i}:`, item);
          items.push(item);
        }
      });

      // Add dry ice packs
      const dryIce = dryIceSpecs[serviceLevel];
      console.log('Adding dry ice packs:', dryIce);
      for (let i = 0; i < dryIce.count; i++) {
        const dryIceItem = {
          id: `dry-ice-${i}`,
          name: `Dry Ice Pack ${i + 1}`,
          length: dryIce.dimensions.length,
          width: dryIce.dimensions.width,
          height: dryIce.dimensions.height,
          weight: dryIce.weight,
          quantity: 1
        };
        console.log(`Adding dry ice item ${i}:`, dryIceItem);
        items.push(dryIceItem);
      }

      console.log('Final items array:', items);
      console.log('Total items count:', items.length);

      // Prepare the request payload
      const requestPayload = {
        items,
        destination_zip: destinationZip,
        service_level: serviceLevel,
        customer_id: user.customerId
      };

      console.log('Request payload:', JSON.stringify(requestPayload, null, 2));

      // Call the enhanced calculation API
      console.log('Calling apiService.calculateShipping...');
      console.log('Debug mode for calculation:', debugMode);
      const result = await apiService.calculateShipping(requestPayload);
      console.log('API response received:', result);

      setCalculation(result);
      setCurrentStep(5); // Go to results step
      console.log('=== CALCULATION DEBUG END - SUCCESS ===');
    } catch (error: any) {
      console.log('=== CALCULATION DEBUG END - ERROR ===');
      console.error('Calculation error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error message:', error.message);
      
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to calculate shipping';
      console.log('Setting error message:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRecommendations = async (recommendation: any) => {
    if (!recommendation.suggested_products || recommendation.suggested_products.length === 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create new cart items list with recommended products added
      const newCartItems = [...cartItems];
      
      for (const suggestedProduct of recommendation.suggested_products) {
        // Check if this is an existing item (quantity increase) or new item
        const existingItemIndex = newCartItems.findIndex(item => item.id === suggestedProduct.product_id);
        
        if (existingItemIndex >= 0) {
          // Add to existing item quantity
          newCartItems[existingItemIndex].quantity += suggestedProduct.recommended_quantity;
          newCartItems[existingItemIndex].totalWeight = newCartItems[existingItemIndex].quantity * newCartItems[existingItemIndex].weight;
          newCartItems[existingItemIndex].totalVolume = newCartItems[existingItemIndex].quantity * newCartItems[existingItemIndex].length * newCartItems[existingItemIndex].width * newCartItems[existingItemIndex].height;
        } else {
          // Add as new item
          const dimensions = suggestedProduct.dimensions.split('x');
          newCartItems.push({
            id: suggestedProduct.product_id,
            name: suggestedProduct.product_name,
            length: parseFloat(dimensions[0]),
            width: parseFloat(dimensions[1]),
            height: parseFloat(dimensions[2]),
            weight: suggestedProduct.weight,
            quantity: suggestedProduct.recommended_quantity,
            totalWeight: suggestedProduct.weight * suggestedProduct.recommended_quantity,
            totalVolume: parseFloat(dimensions[0]) * parseFloat(dimensions[1]) * parseFloat(dimensions[2]) * suggestedProduct.recommended_quantity,
            product: {
              id: parseInt(suggestedProduct.product_id),
              name: suggestedProduct.product_name,
              sku: suggestedProduct.sku,
              length: parseFloat(dimensions[0]),
              width: parseFloat(dimensions[1]),
              height: parseFloat(dimensions[2]),
              weight: suggestedProduct.weight,
              active: true,
              customerId: user?.customerId || 'ait',
              dryIceVolume: 0,
              dryIceWeight: 0,
              priority: 0,
              requiresDryIce: false,
              hold: false,
              onHandPrimary: 0,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          });
        }
      }

      // Update cart items
      setCartItems(newCartItems);

      // Re-run calculation with new items
      const items = [];
      
      // Add regular products
      newCartItems.forEach((cartItem) => {
        for (let i = 0; i < cartItem.quantity; i++) {
          items.push({
            id: `${cartItem.id}-${i + 1}`,
            name: cartItem.name,
            length: cartItem.length,
            width: cartItem.width,
            height: cartItem.height,
            weight: cartItem.weight,
            quantity: 1
          });
        }
      });

      // Add dry ice
      const dryIceCount = dryIceSpecs[serviceLevel].count;
      for (let i = 0; i < dryIceCount; i++) {
        items.push({
          id: `dry-ice-${i + 1}`,
          name: 'Dry Ice',
          length: dryIceSpecs[serviceLevel].dimensions.length,
          width: dryIceSpecs[serviceLevel].dimensions.width,
          height: dryIceSpecs[serviceLevel].dimensions.height,
          weight: dryIceSpecs[serviceLevel].weight,
          quantity: 1
        });
      }

      const requestPayload = {
        items: items,
        destination_zip: destinationZip,
        service_level: serviceLevel.toLowerCase(),
        customer_id: user?.customerId || 'ait'
      };

      console.log('Re-calculating with recommended items:', requestPayload);
      const result = await apiService.calculateShipping(requestPayload);
      console.log('Updated calculation result:', result);
      setCalculation(result);
    } catch (err: any) {
      console.error('Recommendation acceptance error:', err);
      setError(err.response?.data?.detail || 'Failed to apply recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalWeight = cartItems.reduce((sum, item) => sum + item.totalWeight, 0);
  // const totalVolume = cartItems.reduce((sum, item) => sum + item.totalVolume, 0);
  const dryIceWeight = dryIceSpecs[serviceLevel].count * dryIceSpecs[serviceLevel].weight;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4, 5].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep >= step 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {step}
              </div>
              {step < 5 && (
                <div className={`w-16 h-1 mx-2 ${
                  currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-600">
          <span>Destination</span>
          <span>Products</span>
          <span>Review</span>
          <span>Calculate</span>
          <span>Results</span>
        </div>
      </div>

      {/* Step 1: Destination & Service */}
      {currentStep === 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Step 1: Destination & Service</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination ZIP Code
              </label>
              <input
                type="text"
                value={destinationZip}
                onChange={(e) => handleZipCodeChange(e.target.value)}
                placeholder="Enter 5-digit ZIP code"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                maxLength={5}
              />
              {zone && (
                <p className="mt-2 text-sm text-green-600">
                  ✓ Zone {zone} detected
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Service Level
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="overnight"
                    checked={serviceLevel === 'overnight'}
                    onChange={(e) => setServiceLevel(e.target.value as 'overnight')}
                    className="mr-2"
                  />
                  <span>Overnight (1 dry ice pack)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="second_day"
                    checked={serviceLevel === 'second_day'}
                    onChange={(e) => setServiceLevel(e.target.value as 'second_day')}
                    className="mr-2"
                  />
                  <span>2nd Day (2 dry ice packs)</span>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setCurrentStep(2)}
              disabled={!destinationZip || destinationZip.length !== 5}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Next: Select Products
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Product Selection */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Step 2: Select Products</h2>
            
            {/* Loading State */}
            {productsLoading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="mt-2 text-gray-600">Loading products...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <p className="text-red-800">{error}</p>
                <button 
                  onClick={loadProducts}
                  className="mt-2 text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Product Grid */}
            {!productsLoading && !error && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {Array.isArray(products) && products.length > 0 ? products.map((product) => {
                  const cartItem = cartItems.find(item => item.product.id === product.id);
                  const quantity = cartItem?.quantity || 0;
                  
                  return (
                    <div key={product.id} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all duration-200">
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                        <p className="text-sm text-gray-500 mb-2 font-mono">SKU: {product.sku}</p>
                        <div className="bg-gray-50 rounded-lg p-3 mb-3">
                          <p className="text-sm text-gray-700 font-medium mb-1">Dimensions</p>
                          <p className="text-sm text-gray-600">
                            {product.length}" × {product.width}" × {product.height}"
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            Weight: {product.weight} lbs
                          </p>
                        </div>
                      </div>
                      
                      {/* Quantity Selection */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity(product, Math.max(0, quantity - 1))}
                            disabled={quantity === 0}
                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center justify-center text-gray-600 font-medium"
                          >
                            −
                          </button>
                          <span className="w-12 text-center font-semibold text-gray-900">{quantity}</span>
                          <button
                            onClick={() => updateQuantity(product, quantity + 1)}
                            className="w-8 h-8 rounded-full bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center text-white font-medium"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      
                      {/* Action Button */}
                      {quantity > 0 ? (
                        <button
                          onClick={() => removeFromCart(product.id)}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors"
                        >
                          Remove from Selection
                        </button>
                      ) : (
                        <button
                          onClick={() => updateQuantity(product, 1)}
                          className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors"
                        >
                          Add to Selection
                        </button>
                      )}
                    </div>
                  );
                }) : (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <p>No products available</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order Summary */}
          {cartItems.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Order Summary</h3>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
                  {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.product.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">{item.product.name}</h4>
                        <p className="text-sm text-gray-500 font-mono mb-2">SKU: {item.product.sku}</p>
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Dimensions:</span><br />
                            {item.product.length}" × {item.product.width}" × {item.product.height}"
                          </div>
                          <div>
                            <span className="font-medium">Weight:</span><br />
                            {item.product.weight} lbs each
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end space-y-2">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => updateQuantity(item.product, Math.max(0, item.quantity - 1))}
                            className="w-6 h-6 rounded-full bg-gray-300 hover:bg-gray-400 flex items-center justify-center text-gray-700 text-sm"
                          >
                            −
                          </button>
                          <span className="w-8 text-center font-semibold text-gray-900">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product, item.quantity + 1)}
                            className="w-6 h-6 rounded-full bg-indigo-600 hover:bg-indigo-700 flex items-center justify-center text-white text-sm"
                          >
                            +
                          </button>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    
                    {/* Item Totals */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Weight:</span>
                        <span className="font-semibold text-gray-900">{item.totalWeight.toFixed(1)} lbs</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Total Volume:</span>
                        <span className="font-semibold text-gray-900">{item.totalVolume.toFixed(1)} cu in</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Order Totals */}
              <div className="mt-6 pt-6 border-t-2 border-gray-200">
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Total Items:</span>
                  <span>{cartItems.reduce((sum, item) => sum + item.quantity, 0)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Total Weight:</span>
                  <span>{totalWeight.toFixed(1)} lbs</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Dry Ice Weight:</span>
                  <span>{dryIceWeight} lbs</span>
                </div>
                <div className="flex justify-between text-xl font-bold text-indigo-600 mt-2 pt-2 border-t border-gray-300">
                  <span>Grand Total Weight:</span>
                  <span>{(totalWeight + dryIceWeight).toFixed(1)} lbs</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep(1)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Back
            </button>
            <button
              onClick={() => setCurrentStep(3)}
              disabled={cartItems.length === 0}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Next: Review
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {currentStep === 3 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Step 3: Review Order</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-3">Shipping Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Destination:</span>
                  <span>{destinationZip} (Zone {zone})</span>
                </div>
                <div className="flex justify-between">
                  <span>Service:</span>
                  <span className="capitalize">{serviceLevel.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span>Dry Ice Packs:</span>
                  <span>{dryIceSpecs[serviceLevel].count}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-3">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Products:</span>
                  <span>{cartItems.reduce((sum, item) => sum + item.quantity, 0)} items</span>
                </div>
                <div className="flex justify-between">
                  <span>Product Weight:</span>
                  <span>{totalWeight.toFixed(1)} lbs</span>
                </div>
                <div className="flex justify-between">
                  <span>Dry Ice Weight:</span>
                  <span>{dryIceWeight} lbs</span>
                </div>
                <div className="flex justify-between font-medium">
                  <span>Total Weight:</span>
                  <span>{(totalWeight + dryIceWeight).toFixed(1)} lbs</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setCurrentStep(2)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Back
            </button>
            <button
              onClick={() => setCurrentStep(4)}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Next: Calculate
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Calculate */}
      {currentStep === 4 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Step 4: Calculate Optimal Packing</h2>
          
          <div className="text-center py-8">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Running 3D Bin Packing Algorithm</h3>
            <p className="text-gray-600 mb-6">
              Finding the most optimal way to pack your items...
            </p>
            
            <button
              onClick={calculateShipping}
              disabled={loading}
              className="px-8 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Calculating...' : 'Calculate Shipping'}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-between">
            <button
              onClick={() => setCurrentStep(3)}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Step 5: Results */}
      {currentStep === 5 && calculation && (
        <div className="space-y-6">
          {/* Summary Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Calculation Results</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{calculation.total_boxes}</div>
                <div className="text-sm text-blue-800">Boxes</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{calculation.total_weight.toFixed(1)}</div>
                <div className="text-sm text-green-800">Total Weight (lbs)</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{calculation.overall_efficiency.toFixed(1)}%</div>
                <div className="text-sm text-purple-800">Efficiency</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">${calculation.cost_breakdown.total_cost.toFixed(2)}</div>
                <div className="text-sm text-orange-800">Total Cost</div>
              </div>
            </div>

            {/* Cost Breakdown */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Cost Breakdown</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Base Rate:</span>
                  <span>${calculation.cost_breakdown.base_rate.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Material Rate:</span>
                  <span>${calculation.cost_breakdown.material_rate.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Accessories:</span>
                  <span>${calculation.cost_breakdown.accessories.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-medium text-lg border-t pt-2">
                  <span>Total Cost:</span>
                  <span>${calculation.cost_breakdown.total_cost.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Shipping Details */}
            {calculation.debug_info?.cost_calculation?.detailed_rate_info && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-900 mb-3">Shipping Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Zone & Service Information */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                      <span className="text-xs font-medium text-blue-900">Destination Zone</span>
                      <span className="text-sm font-bold text-blue-700">
                        Zone {calculation.debug_info.cost_calculation.detailed_rate_info.zone}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <span className="text-xs font-medium text-green-900">Service Level</span>
                      <span className="text-sm font-bold text-green-700 capitalize">
                        {calculation.debug_info.cost_calculation.detailed_rate_info.service_level.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-purple-50 rounded">
                      <span className="text-xs font-medium text-purple-900">Total Weight</span>
                      <span className="text-sm font-bold text-purple-700">
                        {calculation.debug_info.cost_calculation.detailed_rate_info.total_weight} lbs
                      </span>
                    </div>
                  </div>

                  {/* Rate Information */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                      <span className="text-xs font-medium text-orange-900">Weight Band Used</span>
                      <span className="text-sm font-bold text-orange-700">
                        {calculation.debug_info.cost_calculation.detailed_rate_info.weight_band_used} lbs
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-indigo-50 rounded">
                      <span className="text-xs font-medium text-indigo-900">Rate per Zone</span>
                      <span className="text-sm font-bold text-indigo-700">
                        ${calculation.debug_info.cost_calculation.detailed_rate_info.rate.toFixed(2)}
                      </span>
                    </div>
                    {calculation.debug_info.cost_calculation.detailed_rate_info.zone_1_fallback && (
                      <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                        <span className="text-xs font-medium text-yellow-900">Zone 1 Fallback</span>
                        <span className="text-xs font-bold text-yellow-700">Applied</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Zone Rate Comparison */}
                {calculation.debug_info.cost_calculation.detailed_rate_info.all_zone_rates && (
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <h5 className="text-sm font-medium text-gray-800 mb-2">Zone Rate Comparison</h5>
                    <div className="grid grid-cols-4 gap-1">
                      {Object.entries(calculation.debug_info.cost_calculation.detailed_rate_info.all_zone_rates).map(([zone, rate]) => (
                        <div key={zone} className={`p-1 rounded text-center ${
                          zone === `zone_${calculation.debug_info?.cost_calculation?.detailed_rate_info?.zone}` 
                            ? 'bg-green-100 border border-green-300' 
                            : 'bg-gray-100 border border-gray-200'
                        }`}>
                          <div className="text-xs font-medium text-gray-600 mb-1">
                            {zone.replace('zone_', 'Z')}
                          </div>
                          <div className={`text-xs font-bold ${
                            zone === `zone_${calculation.debug_info?.cost_calculation?.detailed_rate_info?.zone}` 
                              ? 'text-green-800' 
                              : 'text-gray-700'
                          }`}>
                            ${(rate as number).toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Green highlight shows the rate used for your destination
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Packed Boxes */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h4 className="font-medium text-gray-900 mb-4">Packed Boxes</h4>
            <div className="space-y-4">
              {Array.isArray(calculation.packed_boxes) && calculation.packed_boxes.map((packedBox, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="font-medium text-gray-900">{packedBox.box.name}</h5>
                      <p className="text-sm text-gray-600">
                        {packedBox.box.length}" × {packedBox.box.width}" × {packedBox.box.height}"
                      </p>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-gray-600">Utilization: {packedBox.utilization.toFixed(1)}%</div>
                      <div className="text-gray-600">Weight: {packedBox.total_weight.toFixed(1)} lbs</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {Array.isArray(packedBox.items) && packedBox.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                        <span>{item.item_name} (×{item.quantity})</span>
                        <span>{item.dimensions} • {item.weight.toFixed(1)} lbs</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {calculation.recommendations && calculation.recommendations.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Product Recommendations</h3>
              <div className="space-y-4">
                {Array.isArray(calculation.recommendations) && calculation.recommendations.map((rec, index) => (
                  <div key={index} className="border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-blue-800">{rec.box_name}</h4>
                      <div className="text-sm text-blue-600">
                        Current: {rec.current_utilization?.toFixed(1)}% → Potential: {rec.potential_utilization?.toFixed(1)}%
                      </div>
                    </div>
                    <p className="text-blue-700 text-sm mb-3">{rec.message}</p>
                    
                    {rec.remaining_volume && rec.remaining_weight && (
                      <div className="text-xs text-blue-600 mb-3">
                        <p>Remaining Space: {rec.remaining_volume.toFixed(2)} cubic inches, {rec.remaining_weight.toFixed(2)} lbs</p>
                      </div>
                    )}
                    
                    {rec.suggested_products && rec.suggested_products.length > 0 && (
                      <div className="space-y-3">
                        <h5 className="font-medium text-blue-800 text-sm">Suggested Products:</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {Array.isArray(rec.suggested_products) && rec.suggested_products.map((product, productIndex) => (
                            <div key={productIndex} className={`rounded p-3 text-sm ${
                              product.recommendation_type === 'existing_item_quantity' 
                                ? 'bg-green-100 border border-green-300' 
                                : 'bg-blue-100 border border-blue-300'
                            }`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="font-medium text-blue-900">{product.product_name}</div>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  product.recommendation_type === 'existing_item_quantity'
                                    ? 'bg-green-200 text-green-800'
                                    : 'bg-blue-200 text-blue-800'
                                }`}>
                                  {product.recommendation_type === 'existing_item_quantity' ? 'Add More' : 'New Item'}
                                </span>
                              </div>
                              <div className="text-blue-700 text-xs mt-1">
                                <p>Dimensions: {product.dimensions}</p>
                                <p>Weight: {product.weight} lbs</p>
                                <p>Recommended Qty: {product.recommended_quantity} (Max: {product.max_quantity})</p>
                                <p>Additional Volume: {product.additional_volume?.toFixed(2)} cubic inches</p>
                                <p>Additional Weight: {product.additional_weight} lbs</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Recommendation Actions */}
                        <div className="flex items-center space-x-4 pt-3 border-t border-blue-200">
                          <button
                            onClick={async () => {
                              await handleAcceptRecommendations(rec);
                            }}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                          >
                            Accept Recommendations
                          </button>
                          <button
                            onClick={() => {
                              // TODO: Implement decline recommendation functionality
                              console.log('Decline recommendation for box:', rec.box_id);
                            }}
                            className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                          >
                            Decline
                          </button>
                          <span className="text-xs text-blue-600">
                            Add these products to improve box utilization
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Debug Information - Always Show */}
          {calculation?.debug_info && (
            <div className="bg-gray-900 rounded-lg shadow-sm border border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-green-400">Debug Information</h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-sm text-gray-300">Live Debug Data</span>
                </div>
              </div>
              
              {/* Debug Summary */}
              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <h4 className="text-md font-medium text-blue-400 mb-3">Calculation Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-400">{calculation.debug_info.steps?.length || 0}</div>
                    <div className="text-gray-400">Steps</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-400">{calculation.packed_boxes?.length || 0}</div>
                    <div className="text-gray-400">Boxes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-purple-400">{calculation.debug_info.cost_calculation?.zone || 'N/A'}</div>
                    <div className="text-gray-400">Zone</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-400">${calculation.cost_breakdown?.total_cost?.toFixed(2) || '0.00'}</div>
                    <div className="text-gray-400">Total Cost</div>
                  </div>
                </div>
              </div>
              
              {/* Algorithm Steps */}
              {calculation.debug_info.steps && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-blue-400 mb-3">Algorithm Processing Steps</h4>
                  <div className="space-y-2">
                    {calculation.debug_info.steps.map((step: any, index: number) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-gray-800 rounded-lg">
                        <div className="flex-shrink-0">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            step.status === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                          }`}>
                            {step.step}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-white">{step.name}</span>
                            <span className={`text-xs px-2 py-1 rounded ${
                              step.status === 'success' ? 'bg-green-700 text-green-200' : 'bg-red-700 text-red-200'
                            }`}>
                              {step.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-300 mt-1">{step.details}</p>
                          
                          {/* Show additional details for specific steps */}
                          {step.boxes && step.boxes.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-400 mb-1">Available Boxes:</p>
                              <div className="grid grid-cols-2 gap-1">
                                {Array.isArray(step.boxes) && step.boxes.map((box: any, boxIndex: number) => (
                                  <div key={boxIndex} className="text-xs text-gray-300 bg-gray-700 px-2 py-1 rounded">
                                    {box.name}: {box.dimensions} (Max: {box.max_weight}lbs)
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {step.items && step.items.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-400 mb-1">Items Processed:</p>
                              <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto">
                                {Array.isArray(step.items) && step.items.slice(0, 5).map((item: any, itemIndex: number) => (
                                  <div key={itemIndex} className="text-xs text-gray-300 bg-gray-700 px-2 py-1 rounded">
                                    {item.name}: {item.dimensions} ({item.weight}lbs)
                                  </div>
                                ))}
                                {step.items.length > 5 && (
                                  <div className="text-xs text-gray-400 px-2 py-1">
                                    ... and {step.items.length - 5} more items
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Retrieval Debug */}
              {calculation.debug_info.steps && calculation.debug_info.steps.find(step => step.name === 'Product Retrieval') && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-blue-400 mb-3">Product Retrieval Debug</h4>
                  {calculation.debug_info.steps
                    .filter(step => step.name === 'Product Retrieval')
                    .map((step: any, index: number) => (
                      <div key={index} className="p-3 bg-gray-800 rounded-lg mb-2">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-white">Step {step.step}: {step.name}</span>
                          <span className="text-xs px-2 py-1 rounded bg-green-700 text-green-200">
                            {step.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-300 mb-2">{step.details}</p>
                        
                        {step.rbac_applied && (
                          <div className="text-xs text-blue-400 mb-2">
                            <p>RBAC Applied: {step.rbac_applied}</p>
                          </div>
                        )}
                        
                        {step.products_by_customer && (
                          <div className="text-xs text-gray-400 mb-2">
                            <p>Products by Customer:</p>
                            {Object.entries(step.products_by_customer).map(([customer, count]) => (
                              <p key={customer} className="ml-2">• {customer}: {count as number} products</p>
                            ))}
                          </div>
                        )}
                        
                        {step.products && step.products.length > 0 && (
                          <div className="text-xs text-gray-400">
                            <p>Sample Products:</p>
                            {Array.isArray(step.products) && step.products.map((product: any, idx: number) => (
                              <p key={idx} className="ml-2">• {product.name} ({product.customer})</p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {/* Algorithm Debug */}
              {calculation.debug_info.algorithm_debug && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-yellow-400 mb-3">3D Packing Algorithm Details</h4>
                  <div className="space-y-3">
                    {calculation.debug_info.algorithm_debug.strategy_attempts && (
                      <div>
                        <p className="text-sm text-gray-300 mb-2">Strategy Attempts:</p>
                        {Array.isArray(calculation.debug_info.algorithm_debug.strategy_attempts) && calculation.debug_info.algorithm_debug.strategy_attempts.map((attempt: any, index: number) => (
                          <div key={index} className="p-3 bg-gray-800 rounded-lg">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-white">Strategy: {attempt.strategy}</span>
                              <span className={`text-xs px-2 py-1 rounded ${
                                attempt.success ? 'bg-green-700 text-green-200' : 'bg-red-700 text-red-200'
                              }`}>
                                {attempt.success ? 'Success' : 'Failed'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-300">
                              <p>Boxes Used: {attempt.boxes_used}</p>
                              <p>Overflow Items: {attempt.overflow_items}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {calculation.debug_info.algorithm_debug.final_selection && (
                      <div className="p-3 bg-gray-800 rounded-lg">
                        <p className="text-sm font-medium text-white mb-1">Final Selection:</p>
                        <p className="text-xs text-gray-300">
                          Strategy: {calculation.debug_info.algorithm_debug.final_selection.strategy}
                        </p>
                      </div>
                    )}

                    {/* Cost Optimized Steps */}
                    {calculation.debug_info.algorithm_debug.cost_optimized_steps && (
                      <div>
                        <p className="text-sm text-gray-300 mb-2">Cost Optimized Packing Steps:</p>
                        {Array.isArray(calculation.debug_info.algorithm_debug.cost_optimized_steps) && calculation.debug_info.algorithm_debug.cost_optimized_steps.map((step: any, index: number) => (
                          <div key={index} className="p-3 bg-gray-800 rounded-lg mb-2">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-white">Step {index + 1}: {step.step}</span>
                            </div>
                            <p className="text-xs text-gray-300 mb-2">{step.description}</p>
                            {step.details && (
                              <div className="text-xs text-gray-400">
                                {Array.isArray(step.details) && step.details.map((detail: any, detailIndex: number) => (
                                  <div key={detailIndex} className="ml-2">
                                    • {detail.item}: Volume {detail.volume.toFixed(2)}, Weight {detail.weight.toFixed(2)} lbs
                                  </div>
                                ))}
                              </div>
                            )}
                            {step.total_volume && (
                              <div className="text-xs text-gray-400 mt-2">
                                <p>Total Volume: {step.total_volume.toFixed(2)} cubic inches</p>
                                <p>Total Weight: {step.total_weight.toFixed(2)} lbs</p>
                                <p>Item Count: {step.item_count}</p>
                              </div>
                            )}
                            {step.box_name && (
                              <div className="text-xs text-gray-400 mt-2">
                                <p>Selected Box: {step.box_name} (ID: {step.box_id})</p>
                                <p>Box Dimensions: {step.box_dimensions}</p>
                                <p>Box Volume: {step.box_volume.toFixed(2)} cubic inches</p>
                                <p>Box Max Weight: {step.box_max_weight} lbs</p>
                                <p>Utilization: {step.utilization.toFixed(2)}%</p>
                                <p>Weight Efficiency: {step.weight_efficiency.toFixed(2)}%</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Box Evaluations */}
                    {calculation.debug_info.algorithm_debug.box_evaluations && (
                      <div>
                        <p className="text-sm text-gray-300 mb-2">Box Evaluation Process:</p>
                        {Array.isArray(calculation.debug_info.algorithm_debug.box_evaluations) && calculation.debug_info.algorithm_debug.box_evaluations.map((evaluation: any, index: number) => (
                          <div key={index} className="p-3 bg-gray-800 rounded-lg mb-2">
                            {evaluation.step === 'evaluate_boxes' && (
                              <div>
                                <p className="text-sm font-medium text-white mb-2">{evaluation.description}</p>
                                <div className="text-xs text-gray-400">
                                  <p>Required Volume: {evaluation.required_volume.toFixed(2)} cubic inches</p>
                                  <p>Required Weight: {evaluation.required_weight.toFixed(2)} lbs</p>
                                  <p>Total Boxes to Evaluate: {evaluation.total_boxes}</p>
                                </div>
                              </div>
                            )}
                            
                            {evaluation.step === 'evaluation_summary' && (
                              <div className="border-t border-gray-600 pt-3 mt-3">
                                <p className="text-sm font-medium text-white mb-2">{evaluation.description}</p>
                                <div className="text-xs text-gray-400">
                                  <p>Total Boxes Evaluated: {evaluation.total_boxes_evaluated}</p>
                                  <p>Suitable Boxes: {evaluation.suitable_boxes}</p>
                                  <p>Not Suitable Boxes: {evaluation.not_suitable_boxes}</p>
                                  <p>Not Evaluated Boxes: {evaluation.not_evaluated_boxes}</p>
                                  {evaluation.selected_box && (
                                    <div className="mt-2 p-2 bg-green-900 rounded">
                                      <p className="text-green-200 font-medium">Selected Box:</p>
                                      <p>Name: {evaluation.selected_box.name}</p>
                                      <p>Dimensions: {evaluation.selected_box.dimensions}</p>
                                      <p>Volume: {evaluation.selected_box.volume.toFixed(2)} cubic inches</p>
                                      <p>Max Weight: {evaluation.selected_box.max_weight} lbs</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {evaluation.box_name && (
                              <div className="border-l-4 border-gray-600 pl-3 mb-2">
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className="text-sm font-medium text-white">Box #{evaluation.box_index}: {evaluation.box_name}</span>
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    evaluation.status === 'Suitable' ? 'bg-green-700 text-green-200' : 
                                    evaluation.status === 'Not Evaluated' ? 'bg-yellow-700 text-yellow-200' : 
                                    'bg-red-700 text-red-200'
                                  }`}>
                                    {evaluation.status}
                                  </span>
                                  {evaluation.evaluation_stopped && (
                                    <span className="text-xs text-yellow-400">(Evaluation Stopped)</span>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-xs text-gray-400">
                                  <div>
                                    <p className="font-medium text-gray-300 mb-1">Box Specifications:</p>
                                    <p>Dimensions: {evaluation.box_dimensions}</p>
                                    <p>Box Volume: {evaluation.box_volume.toFixed(2)} cubic inches</p>
                                    <p>Max Weight: {evaluation.box_max_weight} lbs</p>
                                  </div>
                                  
                                  <div>
                                    <p className="font-medium text-gray-300 mb-1">Requirements:</p>
                                    <p>Required Volume: {evaluation.required_volume.toFixed(2)} cubic inches</p>
                                    <p>Required Weight: {evaluation.required_weight.toFixed(2)} lbs</p>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-xs text-gray-400 mt-2">
                                  <div>
                                    <p className="font-medium text-gray-300 mb-1">Fit Analysis:</p>
                                    <p>Volume Fits: <span className={evaluation.volume_fits ? 'text-green-400' : 'text-red-400'}>{evaluation.volume_fits ? 'Yes' : 'No'}</span></p>
                                    <p>Weight Fits: <span className={evaluation.weight_fits ? 'text-green-400' : 'text-red-400'}>{evaluation.weight_fits ? 'Yes' : 'No'}</span></p>
                                  </div>
                                  
                                  <div>
                                    <p className="font-medium text-gray-300 mb-1">Utilization:</p>
                                    <p>Volume Utilization: {evaluation.volume_utilization.toFixed(2)}%</p>
                                    <p>Weight Utilization: {evaluation.weight_utilization.toFixed(2)}%</p>
                                  </div>
                                </div>
                                
                                {!evaluation.suitable && (
                                  <div className="mt-2 p-2 bg-red-900 rounded">
                                    <p className="text-red-200 text-xs font-medium">Failure Reason:</p>
                                    <p className="text-red-300 text-xs">{evaluation.failure_reason}</p>
                                    {evaluation.volume_deficit > 0 && (
                                      <p className="text-red-300 text-xs">Volume Deficit: {evaluation.volume_deficit.toFixed(2)} cubic inches</p>
                                    )}
                                    {evaluation.weight_deficit > 0 && (
                                      <p className="text-red-300 text-xs">Weight Deficit: {evaluation.weight_deficit.toFixed(2)} lbs</p>
                                    )}
                                  </div>
                                )}
                                
                                {evaluation.suitable && (
                                  <div className="mt-2 p-2 bg-green-900 rounded">
                                    <p className="text-green-200 text-xs font-medium">✓ This box can accommodate the items</p>
                                    <p className="text-green-300 text-xs">Volume Utilization: {evaluation.volume_utilization.toFixed(2)}%</p>
                                    <p className="text-green-300 text-xs">Weight Utilization: {evaluation.weight_utilization.toFixed(2)}%</p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {evaluation.step === 'box_selected' && (
                              <div className="border-t border-gray-600 pt-3 mt-3">
                                <div className="text-xs text-green-400">
                                  <p className="font-medium">✓ {evaluation.description}</p>
                                  <p>Reason: {evaluation.reason}</p>
                                  <p>Selection Criteria: {evaluation.selection_criteria}</p>
                                </div>
                              </div>
                            )}
                            
                            {evaluation.step === 'no_suitable_box' && (
                              <div className="border-t border-gray-600 pt-3 mt-3">
                                <div className="text-xs text-red-400">
                                  <p className="font-medium">✗ {evaluation.description}</p>
                                  <p>Reason: {evaluation.reason}</p>
                                  <p>Total Boxes Checked: {evaluation.total_boxes_checked}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Item Placements */}
                    {calculation.debug_info.algorithm_debug.item_placements && (
                      <div>
                        <p className="text-sm text-gray-300 mb-2">Item Placement Details:</p>
                        {Array.isArray(calculation.debug_info.algorithm_debug.item_placements) && calculation.debug_info.algorithm_debug.item_placements.map((placement: any, index: number) => (
                          <div key={index} className="p-3 bg-gray-800 rounded-lg mb-2">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-white">{placement.item_name}</span>
                              <span className={`text-xs px-2 py-1 rounded ${
                                placement.placed ? 'bg-green-700 text-green-200' : 'bg-red-700 text-red-200'
                              }`}>
                                {placement.placed ? 'Placed' : 'Not Placed'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-400">
                              <p>Box: {placement.box_number}</p>
                              <p>Item Volume: {placement.item_volume.toFixed(2)} cubic inches</p>
                              <p>Item Weight: {placement.item_weight.toFixed(2)} lbs</p>
                              <p>Box Remaining Volume: {placement.box_remaining_volume.toFixed(2)} cubic inches</p>
                              <p>Box Remaining Weight: {placement.box_remaining_weight.toFixed(2)} lbs</p>
                              {!placement.placed && <p className="text-red-400">Reason: {placement.reason}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Fallback Steps */}
                    {calculation.debug_info.algorithm_debug.fallback_steps && (
                      <div>
                        <p className="text-sm text-gray-300 mb-2">Multi-Box Packing Steps:</p>
                        {Array.isArray(calculation.debug_info.algorithm_debug.fallback_steps) && calculation.debug_info.algorithm_debug.fallback_steps.map((step: any, index: number) => (
                          <div key={index} className="p-3 bg-gray-800 rounded-lg mb-2">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-white">{step.step}</span>
                              {step.is_overflow_round && (
                                <span className="text-xs px-2 py-1 bg-orange-700 text-orange-200 rounded">Overflow Round</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-300 mb-2">{step.description}</p>
                            {step.remaining_items && Array.isArray(step.remaining_items) && (
                              <div className="text-xs text-gray-400">
                                <p>Remaining Items: {step.remaining_items.length}</p>
                                {step.remaining_items.map((item: any, itemIndex: number) => (
                                  <div key={itemIndex} className="ml-2">
                                    • {item.name}: Volume {item.volume?.toFixed(2) || 'N/A'}, Weight {item.weight?.toFixed(2) || 'N/A'} lbs
                                  </div>
                                ))}
                              </div>
                            )}
                            {step.box_name && (
                              <div className="text-xs text-gray-400 mt-2">
                                <p>Selected Box: {step.box_name} (ID: {step.box_id})</p>
                                <p>Box Dimensions: {step.box_dimensions}</p>
                                <p>Box Volume: {step.box_volume.toFixed(2)} cubic inches</p>
                                <p>Box Max Weight: {step.box_max_weight} lbs</p>
                              </div>
                            )}
                            {step.items_packed && (
                              <div className="text-xs text-green-400 mt-2">
                                <p>✓ Successfully packed {step.items_packed} items</p>
                                <p>Box Label: <span className="font-medium">{step.box_label}</span></p>
                                <p>Total Weight: {step.total_weight.toFixed(2)} lbs</p>
                                <p>Total Volume: {step.total_volume.toFixed(2)} cubic inches</p>
                                <p>Utilization: {step.utilization.toFixed(2)}%</p>
                                <p>Weight Efficiency: {step.weight_efficiency.toFixed(2)}%</p>
                                <p>Remaining Items: {step.remaining_items}</p>
                              </div>
                            )}
                            {step.reason && (
                              <div className="text-xs text-red-400 mt-2">
                                <p>✗ {step.reason}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Overflow Boxes Summary */}
                    {calculation.debug_info.algorithm_debug.overflow_boxes && calculation.debug_info.algorithm_debug.overflow_boxes.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-300 mb-2">Box Labels Summary:</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Array.isArray(calculation.debug_info.algorithm_debug.overflow_boxes) && calculation.debug_info.algorithm_debug.overflow_boxes.map((box: any, index: number) => (
                            <div key={index} className={`p-3 rounded-lg ${
                              box.box_label === 'Primary Box' ? 'bg-green-900 border border-green-700' : 'bg-orange-900 border border-orange-700'
                            }`}>
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-medium text-white">Box #{box.box_number}: {box.box_name}</span>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  box.box_label === 'Primary Box' ? 'bg-green-700 text-green-200' : 'bg-orange-700 text-orange-200'
                                }`}>
                                  {box.box_label}
                                </span>
                              </div>
                              <div className="text-xs text-gray-300">
                                <p>Items Packed: {box.items_packed}</p>
                                <p>Overflow Round: {box.is_overflow_round ? 'Yes' : 'No'}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendation Steps */}
                    {calculation.debug_info.algorithm_debug.recommendation_steps && (
                      <div>
                        <p className="text-sm text-gray-300 mb-2">Product Recommendation Process:</p>
                        {Array.isArray(calculation.debug_info.algorithm_debug.recommendation_steps) && calculation.debug_info.algorithm_debug.recommendation_steps.map((step: any, index: number) => (
                          <div key={index} className="p-3 bg-gray-800 rounded-lg mb-2">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-sm font-medium text-white">{step.step}</span>
                            </div>
                            <p className="text-xs text-gray-300 mb-2">{step.description}</p>
                            
                            {step.available_products && (
                              <div className="text-xs text-gray-400">
                                <p>Available Products: {step.available_products}</p>
                              </div>
                            )}
                            
                            {step.box_name && (
                              <div className="text-xs text-gray-400 mt-2">
                                <p>Box: {step.box_name}</p>
                                <p>Current Utilization: {step.current_utilization?.toFixed(2)}%</p>
                                <p>Box Volume: {step.box_volume?.toFixed(2)} cubic inches</p>
                                <p>Used Volume: {step.used_volume?.toFixed(2)} cubic inches</p>
                                <p>Box Max Weight: {step.box_max_weight} lbs</p>
                                <p>Used Weight: {step.used_weight?.toFixed(2)} lbs</p>
                              </div>
                            )}
                            
                            {step.remaining_volume && (
                              <div className="text-xs text-gray-400 mt-2">
                                <p>Remaining Volume: {step.remaining_volume.toFixed(2)} cubic inches</p>
                                <p>Remaining Weight: {step.remaining_weight.toFixed(2)} lbs</p>
                                <p>Will Recommend: {step.will_recommend ? 'Yes' : 'No'}</p>
                                <p>Has Available Products: {step.has_available_products ? 'Yes' : 'No'}</p>
                                <p>Available Products Count: {step.available_products_count || 0}</p>
                                <p>Box Volume: {step.box_volume?.toFixed(2)} cubic inches</p>
                                <p>Used Volume: {step.used_volume?.toFixed(2)} cubic inches</p>
                                <p>Box Max Weight: {step.box_max_weight} lbs</p>
                                <p>Used Weight: {step.used_weight?.toFixed(2)} lbs</p>
                              </div>
                            )}
                            
                            {step.product_name && (
                              <div className="text-xs text-gray-400 mt-2">
                                <p>Product: {step.product_name}</p>
                                <p>Product Volume: {step.product_volume?.toFixed(2)} cubic inches</p>
                                <p>Product Weight: {step.product_weight} lbs</p>
                                <p>Max Quantity: {step.max_quantity}</p>
                                <p>Recommended Quantity: {step.recommended_quantity}</p>
                                <p>Additional Volume: {step.additional_volume?.toFixed(2)} cubic inches</p>
                                <p>Additional Weight: {step.additional_weight} lbs</p>
                              </div>
                            )}
                            
                            {step.suitable_products_count && (
                              <div className="text-xs text-green-400 mt-2">
                                <p>✓ Found {step.suitable_products_count} suitable products</p>
                                <p>Current Utilization: {step.current_utilization?.toFixed(2)}%</p>
                                <p>Potential Utilization: {step.potential_utilization?.toFixed(2)}%</p>
                              </div>
                            )}
                            
                            {step.reason && (
                              <div className="text-xs text-yellow-400 mt-2">
                                <p>Reason: {step.reason}</p>
                                {step.utilization_check !== undefined && (
                                  <div className="mt-1">
                                    <p>Utilization Check: {step.utilization_check ? '✓' : '✗'} ({step.current_utilization?.toFixed(2)}% &lt; 80%)</p>
                                    <p>Volume Check: {step.volume_check ? '✓' : '✗'} ({step.remaining_volume?.toFixed(2)} &gt; 0)</p>
                                    <p>Weight Check: {step.weight_check ? '✓' : '✗'} ({step.remaining_weight?.toFixed(2)} &gt; 0)</p>
                                    <p>Products Check: {step.products_check ? '✓' : '✗'} ({step.available_products_count || 0} products)</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Cost Calculation Debug */}
              {calculation.debug_info.cost_calculation && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-purple-400 mb-3">Cost Calculation Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-800 rounded-lg">
                      <p className="text-sm font-medium text-white mb-2">Zone & Rates</p>
                      <div className="text-xs text-gray-300 space-y-1">
                        <p>Zone: {calculation.debug_info.cost_calculation.zone}</p>
                        <p>Base Rate: ${calculation.debug_info.cost_calculation.base_rate?.toFixed(2) || '0.00'}</p>
                        <p>Material Rate: ${calculation.debug_info.cost_calculation.material_rate?.toFixed(2) || '0.00'}</p>
                        <p>Accessories Rate: ${calculation.debug_info.cost_calculation.accessories_rate?.toFixed(2) || '0.00'}</p>
                      </div>
                      
                      {/* Detailed Rate Information */}
                      {calculation.debug_info.cost_calculation.detailed_rate_info && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <p className="text-sm font-medium text-white mb-2">Detailed Rate Information:</p>
                          <div className="text-xs text-gray-300 space-y-1">
                            <p>Destination ZIP: {calculation.debug_info.cost_calculation.detailed_rate_info.destination_zip}</p>
                            <p>Service Level: {calculation.debug_info.cost_calculation.detailed_rate_info.service_level}</p>
                            <p>Total Weight: {calculation.debug_info.cost_calculation.detailed_rate_info.total_weight} lbs</p>
                            <p>Weight Band Used: {calculation.debug_info.cost_calculation.detailed_rate_info.weight_band_used} lbs</p>
                            <p>Zone 1 Fallback: {calculation.debug_info.cost_calculation.detailed_rate_info.zone_1_fallback ? 'Yes' : 'No'}</p>
                            
                            {/* All Zone Rates */}
                            {calculation.debug_info.cost_calculation.detailed_rate_info.all_zone_rates && (
                              <div className="mt-2">
                                <p className="font-medium text-gray-300 mb-1">All Zone Rates:</p>
                                <div className="grid grid-cols-2 gap-1 text-xs">
                                  {Object.entries(calculation.debug_info.cost_calculation.detailed_rate_info.all_zone_rates).map(([zone, rate]) => (
                                    <div key={zone} className={`p-1 rounded ${
                                      zone === `zone_${calculation.debug_info?.cost_calculation?.zone}` 
                                        ? 'bg-green-900 text-green-200' 
                                        : 'bg-gray-700 text-gray-300'
                                    }`}>
                                      {zone.replace('zone_', 'Zone ')}: ${(rate as number).toFixed(2)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-3 bg-gray-800 rounded-lg">
                      <p className="text-sm font-medium text-white mb-2">Total Cost</p>
                      <div className="text-xs text-gray-300 space-y-1">
                        <p>Total: ${calculation.debug_info.cost_calculation.total_cost?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Box Selection Debug */}
              {calculation.debug_info.box_selection && Object.keys(calculation.debug_info.box_selection).length > 0 && (
                <div className="mb-6">
                  <h4 className="text-md font-medium text-cyan-400 mb-3">Box Selection Process</h4>
                  <div className="p-3 bg-gray-800 rounded-lg">
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                      {JSON.stringify(calculation.debug_info.box_selection, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between">
            <button
              onClick={() => {
                setCurrentStep(1);
                setCalculation(null);
                setCartItems([]);
                setDestinationZip('');
                setError(null);
              }}
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Start Over
            </button>
            <button
              onClick={() => setCurrentStep(2)}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Modify Order
            </button>
            {debugMode && calculation?.debug_info && (
              <button
                onClick={() => setShowDebugWindow(true)}
                className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                View Debug Info
              </button>
            )}
            <button
              onClick={() => setDebugMode(!debugMode)}
              className={`px-6 py-2 rounded-md ${
                debugMode 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              {debugMode ? 'Debug Mode ON' : 'Debug Mode OFF'}
            </button>
          </div>
        </div>
      )}

      {/* Debug Window */}
      {calculation?.debug_info && (
        <DebugWindow
          debugInfo={calculation.debug_info}
          isOpen={showDebugWindow}
          onClose={() => setShowDebugWindow(false)}
        />
      )}
    </div>
  );
};

export default EnhancedCalculator;
